import { splitPageOnParts, injectLinks, injectScripts, customPageControlFunc, collectCustomAddins } from "../utils/pageLoader";
import { getCurrentVehicle } from "../utils/device";
import { IMobileStore } from "../store/mobile/types";
import { Router } from "../router";
import { extend } from "./vanilla";
import rison from "rison";
import { AddinService, IAddinApi } from "../services/addin";
import * as logger from "../utils/logger";
import { ERROR_MESSAGES } from "./errorHandler/error.messages";
import { CancelablePromise as Promise } from "./cancelablePromise";
import { handleApi } from "./error";
import { DriveStore } from "../store/reducers";
import Api from "../services/api";
import { IDashboardAddin } from "../model/Addin";
import { DatabaseCache } from "../services/databaseCache";
import { defaultInternalAddins } from "../constants/internalAddins";
import { UserService } from "./users";
import constants from "../constants";
import type { IInstalledAddin } from "./native/addinNavigationItem";

let currentlyInitializingAddins: string[] = [];

function addToCurrentlyInitializing(name: string) {
    if (currentlyInitializingAddins.indexOf(name) < 0) {
        logger.log(logger.Source.ADDIN, `addinHelper addToCurrentlyInitializing: Adding ${ name } to cache.`);
        currentlyInitializingAddins.push(name);
        return;
    }
    logger.log(logger.Source.ADDIN, `addinHelper addToCurrentlyInitializing: Attempting to add "${ name }" but is already cached.`);
}

function removeFromCurrentlyInitializing(name: string, message?: string) {
    let addinIndex = currentlyInitializingAddins.indexOf(name);
    if (addinIndex > -1) {
        logger.log(logger.Source.ADDIN, `addinHelper removeFromCurrentlyInitializing: Removing ${ name } from cache. ${ message && "Message: " + message }`);
        currentlyInitializingAddins.splice(addinIndex, 1);
        return;
    }
    logger.log(logger.Source.ADDIN, `addinHelper removeFromCurrentlyInitializing: Attempting to remove "${ name }" but is not present in the cache. ${ message && "Message: " + message }`);
}

export interface IAddinMetadata {
    solutionId: string | undefined;
}

export interface IAddinCacheItem {
    func: any;
    metadata: IAddinMetadata;
    dom: HTMLElement[] | string | null;
    addinContent: IAddinContent;
    isStartedOnDashboard: boolean;
    isStartedOnAddinPage: boolean;
}

export enum AddinCacheSource {
    STARTUP = "Startup Callback",
    INIT = "Addin Initialize Callback"
}

export interface IAddinContent {
    bodyContent: string;
    title: string;
    scripts?: any[];
    links?: any[];
}

export class AddinsCache {
    addins = {};

    add(name: string, item: IAddinCacheItem, source: AddinCacheSource) {
        logger.log(
            logger.Source.ADDIN,
            `AddinsCache: Adding ${ name } to cache. Source: ${ source }`
        );
        this.addins[name] = item;
    }

    updateDOM(name: string, dom: HTMLElement[]) {
        if (this.addins.hasOwnProperty(name)) {
            this.addins[name].dom = dom;
        }
    }

    get(name): IAddinCacheItem {
        return this.addins[name];
    }

    remove(name: string): void {
        delete this.addins[name];
    }

    clear(): void {
        currentlyInitializingAddins = [];
        this.addins = {};
    }
}

export const addinsCache = new AddinsCache();

export function getFullHash(addinName: string): string {
    return `addin,(addinName:${ addinName })`;
}

function getAddinPrefix(addinConfig, api) {
    let prefix = addinConfig.mainFileFolder;
    let isSameDomain = addinConfig.mainFileFolder.indexOf("\/\/") === -1;

    if (isSameDomain) {
        let cred = api && api.getCurrentCredentials();
        prefix = cred ? `https://${ cred.server.replace(/\S*:\/\//, "").replace(/\/$/, "") }/${ addinConfig.mainFileFolder }` : "";
    }

    return prefix;
}

export function getExtendedAppState(mobile: DeepReadonly<IMobileStore>, router: Router): IMobileStore {
    let appState = extend(true, {}, mobile) as IMobileStore;
    appState.device = getCurrentVehicle();
    appState.setDevice = (device) => {
        let deviceId = device && device.id ? device.id : "NoDeviceId";
        router.navigate(`workflow,${ rison.encode({
            logout: false,
            step: "selectVehicle",
            changeVehicle: false,
            newDeviceId: deviceId
        }) }`, true);
    };
    return appState;
}

// Used in unit tests to confirm an addin has only started up once
export let blockAttemptCount = 0;
export let initializeCount = 0;
export function incrementBlockCount() {
    blockAttemptCount++;
}
export function incrementInitializeCount() {
    initializeCount++;
}

/**
 * For safety, we want to wrap all Addin shutdown methods in promises to ensure that their behaviour is standardized and that they can be adequately handled
 * in the case of errors
 * @returns A promisified version of the provided addin shutdown method
 */
export function promisifyAddinShutdownMethod(fn: Function, addinApi: IAddinApi, appState: IMobileStore, addinName: string) {
    return new Promise((resolve, reject) => {
        try {
            resolve(fn(addinApi, appState));
        } catch (err) {
            reject(err);
        }
    }).catch(err => {
        throw new Error(`Error executing shutdown method for ${ addinName }. Message: ${ err.message ?? err }`);
    });
}

export const createInsecureAddinsFilter = (isUnsignedAllowed: boolean, addinJson) => {
    if (isUnsignedAllowed || defaultInternalAddins.find(intAddin => intAddin.url.indexOf(addinJson.downloadUrl) > -1)) {
        return true;
    }

    return addinJson && addinJson.isSigned;
};

export const createUserAddinsAccessFilter = (alc: string[]) => addinJson => {
    if (addinJson && addinJson.enableViewSecurityId) {
        return alc.indexOf(addinJson.name || "") >= 0;
    }

    return true;
};

export const checkInternalAddinAccessFilter = (acl: string[], viewSecurityIdentifier: string) => {
    return acl.indexOf(viewSecurityIdentifier) >= 0;
};

export function getAddinConfig(addinName: string, databaseCache: DatabaseCache, currentUser) {
    let addins = collectCustomAddins(databaseCache.getAddins(), currentUser);
    let addinConfig = addins.filter(e => {
        let comparison = (e.addinPath as string) + (e.path as string);
        return addinName === comparison;
    });

    return addinConfig[0] || null;
}

/**
 * Gets the addin config and returns the path that is used for its local storage key.
 * @param name The name of the addin.
 * @param databaseCache The database cache.
 */
export function getAddinStorageKey(name: string, databaseCache: DatabaseCache, currentUser) {
    let config = getAddinConfig(name, databaseCache, currentUser);
    return `${ config.addinPath }${ config.path }`;
}

/**
 * The Drive add-ins the database configures for `currentUser` (the `DriveAppLink/` items, the only ones Drive loads),
 * each with the name Drive calls it by and the URL of its file as configured. An add-in whose configuration could
 * not be read is left out.
 */
export function listInstalledAddins(databaseCache: DatabaseCache, currentUser): IInstalledAddin[] {
    return collectCustomAddins(databaseCache.getAddins(), currentUser)
        .filter(addin => addin.path === "DriveAppLink/" && typeof addin.rawUrl === "string" && addin.rawUrl !== "")
        .map(addin => ({ name: `${ addin.addinPath }${ addin.path }`, url: addin.rawUrl }));
}

export interface IAddinInformation {
    metadata: IAddinMetadata;
    addinContent: IAddinContent;
}

function getAddinHtml(addinConfig, databaseCache: DatabaseCache) {
    return new Promise((resolve, reject) => {
        let addinHtml = databaseCache.getCachedAddin(addinConfig);
        if (addinHtml && addinHtml.html !== "") {
            resolve(addinHtml.html);
        } else {
            databaseCache.requestAndSave(addinConfig, resolve, reject);
        }
    });
}

export function downloadAddin(addin: IAddinCacheItem | null, databaseCache: DatabaseCache, name: string, api, currentUser, config?: any): Promise<IAddinInformation> {
    if (addin) {
        return Promise.resolve(({ metadata: addin.metadata, addinContent: addin.addinContent }));
    }

    let addinConfig = config ? config : getAddinConfig(name, databaseCache, currentUser);
    return getAddinHtml(getAddinStorageKey(name, databaseCache, currentUser), databaseCache).then(addinHtml => {
        let prefix = getAddinPrefix(addinConfig, api);
        let addinContent = splitPageOnParts(addinHtml, prefix);

        injectLinks(addinContent.links);

        return injectScripts(addinContent.scripts, prefix, getFullHash(name)).then(() => ({ addinContent, metadata: Object.freeze({ solutionId: addinConfig.solutionId }) }));
    });
}

export const collectDashboardAddins = (databaseCache: DatabaseCache, userService: UserService): IDashboardAddin[] => {
    let addins = collectCustomAddins(databaseCache.getAddins(), userService.getCurrentUser());
    let parsedAddins: IDashboardAddin[] = [];

    if (addins && addins.length) {
        for (let i = 0; i < addins.length; i++) {
            let addin = addins[i];
            if (!(addin.path && addin.path === "DriveAppLink/")) {
                continue;
            }
            parsedAddins.push({
                id: btoa(`${ addins[i].addinPath }${ addins[i].path }`),
                imgUrl: addin.icon,
                name: addin.menuName,
                status: "",
                hash: ""
            });
        }
        return parsedAddins;
    }

    return [];
};

/**
 * Parses and executes `startup` lifecycle methods for Drive App addins.
 * Used in the non-deferred path
 */
export function executeStartupAddins(
    addins: IDashboardAddin[],
    databaseCache: DatabaseCache,
    userService: UserService,
    store: DriveStore,
    router: Router,
    addinService: AddinService,
    api: Api
) {
    let addinStartupPromises: Promise[] = [];

    for (let i = 0; i < addins.length; i++) {
        let addinName = atob(addins[i].id);
        let addinConfig = getAddinConfig(addinName, databaseCache, userService.getCurrentUser());
        if (!addinConfig) {
            logger.log(logger.Source.ADDIN, `Addins Startup: Failed to find addin configuration for ${ addinName }.`);
            continue;
        }

        if (!addinConfig.onStartup) {
            continue;
        }

        let isAddinCurrentlyInitializing = currentlyInitializingAddins.indexOf(addinName) > -1;
        if (isAddinCurrentlyInitializing) {
            incrementBlockCount();
            continue;
        }

        let cachedAddin = addinsCache.get(addinName);
        if (cachedAddin) {
            continue;
        }

        addinStartupPromises.push(new Promise(resolve => {
            addToCurrentlyInitializing(addinName);
            downloadAddin(cachedAddin, databaseCache, addinName, api, userService.getCurrentUser())
                .then(({ addinContent, metadata }) => {
                    let addinDefinition = customPageControlFunc(getFullHash(addinName));
                    let addinFunction = addinDefinition && addinDefinition(api, null, metadata);
                    if (addinFunction) {
                        let startupMethod: Function;
                        if (addinFunction.startup && addinFunction.startup instanceof Function) {
                            startupMethod = addinFunction.startup;
                        } else {
                            logger.log(
                                logger.Source.ADDIN_STARTUP,
                                `Addins Startup: ${ addinName } is flagged as "startup = true", but the addin either does not have a startup method declared or startup is not a function. Replacing with placeholder startup method`
                            );
                            startupMethod = (_addinApi, _appState, cb) => { cb(); };
                        }
                        try {
                            logger.log(logger.Source.ADDIN_STARTUP, `Addins Startup: Executing Startup Method for ${ addinName }.`);
                            startupMethod(
                                addinService.getAddinApi(addinName),
                                getExtendedAppState(store.getState().mobile, router),
                                () => {
                                    addinsCache.add(
                                        addinName,
                                        {
                                            func: addinFunction,
                                            dom: null,
                                            metadata,
                                            addinContent,
                                            isStartedOnDashboard: true,
                                            isStartedOnAddinPage: false
                                        },
                                        AddinCacheSource.STARTUP
                                    );
                                    removeFromCurrentlyInitializing(addinName, "Removed on startup callback.");
                                    resolve();
                                }
                            );
                            incrementInitializeCount();
                        } catch (err) {
                            handleApi(err, true);
                            removeFromCurrentlyInitializing(addinName, "Removed on error executing startup method.");
                            resolve();
                        }
                    } else {
                        removeFromCurrentlyInitializing(addinName, "Failed to retrieve addin definition");
                        resolve();
                    }
                }).catch((error) => {
                    removeFromCurrentlyInitializing(addinName, `Error in 'downloadAddin' -> ${ error && error.message }`);
                    const errorMessage = error && error.message || "Undefined Error";
                    if (errorMessage.indexOf("Unknown addin's error") === -1) {
                        logger.error(logger.Source.API, `${ ERROR_MESSAGES.ADDIN_INACCESSIBLE }. Message: ${ errorMessage }`);
                    }
                    resolve();
                });
        }));
    }

    if (addinStartupPromises.length) {
        void Promise.all(addinStartupPromises);
    }
}

export const getAddinNameFromUrl = (hash: string) => {
    let decoded: string;
    try {
        decoded = decodeURIComponent(hash);
    } catch {
        return null;
    }
    const addinNameMatch = decoded.match(/addinName:([^,)]*)/);
    if (addinNameMatch) {
        try {
            return atob(addinNameMatch[1]).split("/")[1] || null;
        } catch {
            return null;
        }
    }

    return null;
};

/**
 * Minimal service dependencies required by logout addin shutdown functions.
 */
export interface ILogoutAddinServices {
    userService: UserService;
    store: DriveStore;
    addinService: AddinService;
    api: Api;
    router: Router;
    databaseCache?: DatabaseCache;
}

interface IParsedAddin {
    addinName: string;
    meta: IAddinMetadata;
}

/**
 * Executes add-in shutdown methods with a timeout.
 * Only runs if there is exactly 1 active user (last user logging out). The lifecycle registrations stay in
 * place: logout can still fail after this. Session.close drops them (AddinService.releaseAllAddinEvents) once
 * the local storage is cleared.
 */
export function executeLogoutAddins(services: ILogoutAddinServices): Promise<void> {
    const { userService, store, addinService, api, router } = services;
    const databaseCache = services.databaseCache;
    const currentUser = userService.getCurrentUser();
    const addins = databaseCache
        ? collectCustomAddins(databaseCache.getAddins(), currentUser)
        : null;

    if (userService.getActiveUserNames().length !== 1) {
        return Promise.resolve();
    }
    if (!addins || !databaseCache) {
        return Promise.resolve();
    }

    const promises: Promise<void>[] = [];
    const appState = getExtendedAppState(store.getState().mobile, router);
    const parseAddinPromises: Promise<IParsedAddin>[] = [];

    for (let i = 0; i < addins.length; i++) {
        const addin = addins[i];

        if (!(addin.path && addin.path === "DriveAppLink/")) {
            continue;
        }

        const addinName = `${ addin.addinPath }${ addin.path }`;
        const addinConfig = getAddinConfig(addinName, databaseCache, currentUser);
        if (addinConfig && addinConfig.onShutdown) {
            const cachedAddin = addinsCache.get(addinName);
            if (cachedAddin && cachedAddin.func) {
                if (cachedAddin.func.shutdown instanceof Function) {
                    promises.push(
                        promisifyAddinShutdownMethod(
                            cachedAddin.func.shutdown,
                            addinService.getAddinApi(addinName),
                            appState,
                            addinName
                        ).catch(err => {
                            handleApi(err, true);
                        })
                    );
                }
            } else {
                parseAddinPromises.push(
                    downloadAddin(addinsCache.get(addinName), databaseCache, addinName, api, addinConfig)
                        .then(() => ({ addinName, meta: Object.freeze({ solutionId: addin.solutionId }) }))
                );
            }
        }
    }

    if (parseAddinPromises.length) {
        return Promise.all(parseAddinPromises).then((addinContent): Promise<void> => {
            for (let i = 0; i < addinContent.length; i++) {
                const { addinName, meta } = addinContent[i];
                const addinDefinition = customPageControlFunc(getFullHash(addinName));
                const addinFunction = addinDefinition(api, null, meta);
                if (addinFunction.shutdown instanceof Function) {
                    promises.push(
                        promisifyAddinShutdownMethod(
                            addinFunction.shutdown,
                            addinService.getAddinApi(addinName),
                            appState,
                            addinName
                        ).catch(err => {
                            handleApi(err, true);
                        })
                    );
                }
            }

            if (promises.length) {
                return Promise.race([
                    new Promise<void>(resolve => setTimeout(resolve, constants.LOGOUT_ADDIN_EXECUTION_TIMELIMIT)),
                    Promise.all(promises).then(() => undefined)
                ]);
            }
            return Promise.resolve();
        }).catch((e: Error) => {
            handleApi(e, true);
            return Promise.resolve();
        });
    }

    if (promises.length) {
        return Promise.race([
            new Promise<void>(resolve => setTimeout(resolve, constants.LOGOUT_ADDIN_EXECUTION_TIMELIMIT)),
            Promise.all(promises).then(() => undefined)
        ]);
    }
    return Promise.resolve();
}
